import {
  GetQConfigByIdDocument,
  QBankStructureLevelCategory,
  useAddQConfigStructuresMutation,
  useGetQBankStructureLevelItemsByQBankStructureIdQuery,
  useGetQBankStructureLevelsByQBankStructureIdQuery,
} from '@exam-management/graphql';
import { useParams } from '@exam-management/router';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, DialogActions, DialogContent, Divider, Flex, Form } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { QBSLevelItemSelectionProvider } from '../../providers';
import { QBSLevelItemSelection } from '../qbs-level-item-selection';
import { SearchQBSSelection } from '../search-qbs-selection';

const AddQBankToQConfigSchema = z.object({
  qbankSelection: z.object({
    value: z.string(),
    label: z.string(),
  }),
  levelItems: z.record(z.string(), z.boolean()),
});

export type AddQBankToQConfigFormValues = z.infer<typeof AddQBankToQConfigSchema>;

export interface AddQBankToQConfigFormProps {
  onClose: () => void;
}

export default function AddQBankToQConfigForm(props: Readonly<AddQBankToQConfigFormProps>) {
  const { onClose } = props;
  const { qconfigId } = useParams('/exam-courses/:courseId/qconfigs/:qconfigId');
  const formContext = useForm<AddQBankToQConfigFormValues>({
    defaultValues: {
      qbankSelection: {
        value: '',
        label: '',
      },
      levelItems: {},
    },
    resolver: zodResolver(AddQBankToQConfigSchema),
  });
  const { showSnackbar } = useSnackbar();

  const qBankSelection = formContext.watch('qbankSelection')?.value;
  const { data: getQBSLevelItemsResult, loading: isGetQBSLevelItemsLoading } =
    useGetQBankStructureLevelItemsByQBankStructureIdQuery({
      variables: {
        qBankStructureId: qBankSelection,
        options: {
          categories: [
            QBankStructureLevelCategory.Part,
            QBankStructureLevelCategory.Subject,
            QBankStructureLevelCategory.Group,
            QBankStructureLevelCategory.Chapter,
          ],
        },
      },
      skip: !qBankSelection,
    });

  const { data: getQBSLevelResult, loading: isGetQBSLevelLoading } =
    useGetQBankStructureLevelsByQBankStructureIdQuery({
      variables: {
        qBankStructureId: qBankSelection,
      },
      skip: !qBankSelection,
    });

  const qbsLevelItems =
    getQBSLevelItemsResult?.qbsGetQBankStructureLevelItemsByQBankStructureId ?? [];
  const qbsLevels = getQBSLevelResult?.qbsGetQBankStructureLevelsByQBankStructureId ?? [];

  const levelItems = Object.values(formContext.watch('levelItems'));
  const isSubmitDisabled = useMemo(() => {
    if (!qBankSelection) {
      return true;
    }

    const isSomeLevelItemsSelected = levelItems.some((value) => value);
    return !isSomeLevelItemsSelected;
  }, [qBankSelection, levelItems]);

  const [addQConfigStructures, { loading: isAddQConfigStructuresLoading }] =
    useAddQConfigStructuresMutation({
      onCompleted: () => {
        showSnackbar({
          message: 'QBank Structure added successfully',
          variant: 'success',
        });
      },
      onError: () => {
        showSnackbar({
          message: 'Unable to add QBank',
          variant: 'error',
        });
      },
      refetchQueries: [
        {
          query: GetQConfigByIdDocument,
          variables: {
            getQConfigByIdInput: {
              qConfigId: qconfigId,
            },
          },
        },
      ],
      awaitRefetchQueries: true,
    });

  const handleSubmitAddQConfigStructures = formContext.handleSubmit(async (values) => {
    const qbsId = values.qbankSelection.value;

    const levelItems = Object.entries(values.levelItems)
      .filter(([, value]) => value)
      .map(([key]) => key);

    const chapterLevelId = qbsLevels.find(
      (qbsLevel) => qbsLevel.category === QBankStructureLevelCategory.Chapter,
    )?.id;
    const chapterLevelItems = levelItems.filter(
      (levelItem) =>
        qbsLevelItems.find((qbsLevelItem) => qbsLevelItem.id === levelItem)
          ?.qBankStructureLevelId === chapterLevelId,
    );

    await addQConfigStructures({
      variables: {
        addQConfigStructuresInput: {
          qConfigId: qconfigId,
          selectedQBankStructureId: qbsId,
          selectedQBankStructureLevelItemIds: chapterLevelItems,
        },
      },
    });

    onClose();
  });

  return (
    <FormProvider {...formContext}>
      <Form onSubmit={handleSubmitAddQConfigStructures}>
        <DialogContent
          sx={(theme) => ({
            display: 'flex',
            flexDirection: 'column',
            py: theme.spacing(6),
            gap: theme.spacing(6),
            maxHeight: '600px',
            overflow: 'auto',
          })}
        >
          <Flex
            sx={(theme) => ({
              bgcolor: theme.palette.grey[50],
              border: '1px solid',
              borderColor: theme.palette.grey[200],
              borderRadius: theme.shape.rounded(2),
              p: theme.spacing(4),
            })}
          >
            <SearchQBSSelection name='qbankSelection' />
          </Flex>
          {qBankSelection ? (
            <QBSLevelItemSelectionProvider
              levels={qbsLevels}
              levelItems={qbsLevelItems}
              loading={isGetQBSLevelItemsLoading || isGetQBSLevelLoading}
            >
              <QBSLevelItemSelection />
            </QBSLevelItemSelectionProvider>
          ) : null}
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button
            variant='outlined'
            color='secondary'
            onClick={onClose}
            disabled={isAddQConfigStructuresLoading}
          >
            Cancel
          </Button>
          <Button type='submit' disabled={isSubmitDisabled} loading={isAddQConfigStructuresLoading}>
            Add QBank
          </Button>
        </DialogActions>
      </Form>
    </FormProvider>
  );
}
